/* eslint-disable react-hooks/exhaustive-deps */
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Alert, Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import Table from "components/Table";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import usePaginationHook from "hooks/usePagination";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteDailyRewardCycle,
  getDailyRewardCycles,
  updateDailyRewardCycleStatus,
} from "services/dailyRewardCycleService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import {
  formatWeekRange,
  nextFreeWeekStart,
  weekKey,
} from "utils/dailyRewardWeek";
import {
  CycleRow,
  getCyclePhase,
  getDailyRewardCycleColumns,
} from "utils/tableHelper/dailyRewardCyclesTableHelper";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
  },
}));

const RowActions = ({
  data,
  actionHandlers: { handleStatus = (_row: any) => {}, handleDelete = (_row: any) => {} },
}: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleEdit = (event: any) => {
    handleClose(event);
    navigate(
      configData.routes.dailyRewardCycles.edit?.replace(":id", data?._id),
    );
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.DAILYREWARDCYCLES.DELETE_ROLES,
  );
  const hasStatusPermission = sessionHelper.hasRole(
    PermittedRoles.DAILYREWARDCYCLES.STATUS_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {/* An ended week is history — the editor opens it read-only, so the action says so. */}
        <MenuItem onClick={handleEdit}>
          {getCyclePhase(data) === "past" ? "View" : "Edit"}
        </MenuItem>
        {/* The default cycle is the fallback for every operator no weekly cycle targets, so it
            can neither be deactivated nor deleted (the server rejects both as well). */}
        {!data.isDefault && hasStatusPermission && (
          <MenuItem
            onClick={(e) => {
              handleClose(e);
              handleStatus(data);
            }}
          >
            {data.isActive ? "Deactivate" : "Activate"}
          </MenuItem>
        )}
        {!data.isDefault && hasDeletePermission && (
          <MenuItem
            onClick={(e) => {
              handleClose(e);
              handleDelete(data);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const DailyRewardCycles = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<CycleRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  // Both come from the whole collection rather than the page: the default may sit on another page,
  // and a week taken by a cycle three pages down is still taken.
  const [hasDefault, setHasDefault] = useState(false);
  const [takenWeeks, setTakenWeeks] = useState<string[]>([]);
  const [pendingDelete, setPendingDelete] = useState<CycleRow | null>(null);

  const { onChangePage, onPageSizeChange, pageNo, pageSize } = usePaginationHook(
    { totalItems },
  );

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.DAILYREWARDCYCLES.UPDATE_ROLES,
  );
  const hasCreatePermission = sessionHelper.hasRole(
    PermittedRoles.DAILYREWARDCYCLES.CREATE_ROLES,
  );

  // The week Add Week will land on, so the button says which one before the admin commits to it.
  const nextWeek = nextFreeWeekStart(takenWeeks);

  const columns = getDailyRewardCycleColumns(hasEditPermission);

  const rowHref = (data: any) =>
    hasEditPermission
      ? configData.routes.dailyRewardCycles.edit?.replace(":id", data?._id)
      : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getDailyRewardCycles({ pageNo, pageSize });
      const data = resp?.data;

      // The page arrives already ordered by the server — default, running week, upcoming, then
      // ended — so re-sorting here would only reshuffle within the page and break that order.
      setListData(data?.items ?? []);
      setTotalItems(Number(data?.totalItems ?? 0));
      setHasDefault(!!data?.hasDefault);
      setTakenWeeks((data?.takenWeeks ?? []).map((week: string) => weekKey(week)));
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (row: CycleRow) => {
    try {
      const resp = await updateDailyRewardCycleStatus(row._id);
      showSnackbar(resp.message, "success");
      fetchData();
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      const resp = await deleteDailyRewardCycle(pendingDelete._id);
      showSnackbar(resp.message, "success");
      fetchData();
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setPendingDelete(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize]);

  // The default cycle has to exist before any week can be added: it is the fallback every week
  // without a cycle of its own falls back to, so the CMS only ever offers one of the two buttons.
  const createLabel = hasDefault
    ? `Add Week (${formatWeekRange(nextWeek)})`
    : "Add Default";

  return (
    <ContentShell
      title="Daily Reward Cycles"
      loading={loading}
      actions={
        hasCreatePermission && (
          <CustomButton
            onClick={() =>
              navigate(
                `${configData.routes.dailyRewardCycles.create}?type=${
                  hasDefault ? "week" : "default"
                }`,
              )
            }
          >
            {createLabel}
          </CustomButton>
        )
      }
    >
      {!hasDefault && !loading && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          <Typography variant="body2">
            Start with the <b>default cycle</b>. It is what every player sees in
            any week that has no cycle of its own, so it must exist before a
            week can be added.
          </Typography>
        </Alert>
      )}

      <Box className={styles.container}>
        <Table
          columns={columns}
          data={listData}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={totalItems}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          highlightAltRow={true}
          actions={RowActions}
          rowHref={rowHref}
          actionHandlers={{ handleStatus, handleDelete: setPendingDelete }}
        />
      </Box>

      <DeleteConsentDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        itemName={pendingDelete?.name}
        itemDetails={
          pendingDelete && getCyclePhase(pendingDelete) === "live"
            ? `This week is running right now (${formatWeekRange(
                pendingDelete.startDate,
              )}). Players will fall back to the default cycle immediately, so the rest of the week may show different rewards. Days they have already claimed stay claimed and cannot be claimed again.`
            : undefined
        }
      />
    </ContentShell>
  );
};

export default DailyRewardCycles;
